import { useEffect, useState } from 'react'
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native'
import { Icon, Text, useTheme } from 'react-native-paper'
import { generateSlug } from '@/hooks/useSlug'
import type { IconType } from '@/lib/types'

interface CollapsibleSectionProps {
    title: string
    icon?: IconType
    defaultExpanded?: boolean
    children?: React.ReactNode
    rightContent?: React.ReactNode
    rightControls?: React.ReactNode
    isLast?: boolean
    containerStyle?: ViewStyle
    onExpandedChange?: (expanded: boolean) => void
    collapsible?: boolean
}

export function CollapsibleSection({
    title,
    icon,
    defaultExpanded = false,
    children,
    rightContent,
    rightControls,
    isLast = false,
    containerStyle,
    onExpandedChange,
    collapsible = true,
}: CollapsibleSectionProps) {
    const theme = useTheme()
    const [expanded, setExpanded] = useState(defaultExpanded)

    useEffect(() => {
        onExpandedChange?.(expanded)
    }, [expanded, onExpandedChange])

    const canExpand = collapsible && children
    const showBottomBorder = !isLast || expanded

    const headerContent = (
        <View style={styles.headerContent}>
            <View style={styles.titleRow}>
                {icon && <Icon source={icon} size={16} color={theme.colors.onSurfaceVariant} />}
                <Text variant="titleMedium" style={{ fontWeight: '600' }}>
                    {title}
                </Text>
            </View>
            {rightContent && <View style={styles.rightContent}>{rightContent}</View>}
        </View>
    )

    return (
        <View
            style={[
                styles.container,
                expanded && !!canExpand && { backgroundColor: `${theme.colors.surfaceVariant}40` },
                containerStyle,
            ]}
        >
            {canExpand ? (
                <Pressable
                    onPress={() => setExpanded(!expanded)}
                    style={[
                        styles.header,
                        { borderBottomColor: theme.colors.outlineVariant },
                        showBottomBorder && { borderBottomWidth: 1 },
                    ]}
                >
                    {headerContent}
                    {rightControls}
                    <Icon
                        testID={`collapsible-${generateSlug(title)}-toggle`}
                        source={expanded ? 'chevron-up' : 'chevron-down'}
                        size={24}
                        color={theme.colors.onSurfaceVariant}
                    />
                </Pressable>
            ) : (
                <View
                    style={[
                        styles.header,
                        { borderBottomColor: theme.colors.outlineVariant },
                        showBottomBorder && { borderBottomWidth: 1 },
                    ]}
                >
                    {headerContent}
                    {rightControls}
                </View>
            )}

            {expanded && canExpand && (
                <View
                    style={[
                        styles.contentContainer,
                        !isLast && {
                            borderBottomColor: theme.colors.outlineVariant,
                            borderBottomWidth: 1,
                        },
                    ]}
                >
                    {children}
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        borderRadius: 0,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
        paddingHorizontal: 16,
    },
    headerContent: {
        flex: 1,
        gap: 4,
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    rightContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginTop: 4,
    },
    contentContainer: {
        paddingTop: 8,
        paddingBottom: 16,
        paddingHorizontal: 16,
    },
})
